import { setContext } from '@apollo/client/link/context';
import * as Sentry from '@sentry/react';
import {
  AuthProvider,
  GithubAuthProvider,
  GoogleAuthProvider,
  TwitterAuthProvider,
  getIdToken,
  sendPasswordResetEmail,
  signInWithPopup,
  verifyBeforeUpdateEmail,
} from 'firebase/auth';

import { fbAuth } from 'support/firebase/firebase';
import { QueryParams } from 'support/QueryParams';

import { memoryStorage } from '../storage';

// Login with a social provider.
export const socialLogin = (provider: 'github' | 'google' | 'twitter') => {
  let authProvider: AuthProvider;

  switch (provider) {
    case 'github':
      authProvider = new GithubAuthProvider().addScope('email');
      break;

    case 'google':
      authProvider = new GoogleAuthProvider().addScope('email');
      break;

    case 'twitter':
      authProvider = new TwitterAuthProvider();
      break;
  }

  return signInWithPopup(fbAuth, authProvider);
};

// Reset a users password.
export const resetPassword = (email: string) => sendPasswordResetEmail(fbAuth, email);

// Sends an email to the users new email, with a link to confirm changing the users email.
export const sendUpdateEmailConfirmation = (email: string) => {
  const { currentUser } = fbAuth;
  if (!currentUser) return;

  verifyBeforeUpdateEmail(currentUser, email);
};

/**
 * This Apollo link adds a header to the HTTP request containing the embed token, if any.
 *
 * The embed token can be passed as a query parameter to the splash screen, which stores the token
 * in the session storage. This token allows users to play private or unpublished content.
 *
 * We decided to pass the token as a HTTP header, fetched from the session storage, to avoid altering
 * every query/mutation used by the learn environment with a embed_token argument.
 */
export const embedTokenLink = setContext((_, { headers }) => {
  let embedToken;

  // We use the key slug as a unique key to the embed token for a track
  // this safeguards use cases where more than one embed track is used
  // by the user.
  // The key for the embed token will always be in the same position by
  // design. If we change our embed urls this should be changed.
  // e.g.: https://play.instruqt.com/embed/organization/tracks/track-slug
  const embedTokenKey = `embedToken-${window.location.pathname.split('/')[4]}`;
  const newToken = new URLSearchParams(window.location.search).get(QueryParams.EMBED_TOKEN);

  try {
    embedToken = newToken ?? sessionStorage.getItem(embedTokenKey);

    if (embedToken) {
      sessionStorage.setItem(embedTokenKey, embedToken);
    }
  } catch {
    embedToken = newToken ?? memoryStorage.getItem(embedTokenKey);

    if (embedToken) {
      memoryStorage.setItem(embedTokenKey, embedToken);
    }
  }

  if (!embedToken) {
    return {
      headers,
    };
  }

  return {
    headers: {
      ...headers,
      'X-Embed-Token': embedToken,
    },
  };
});

export const authLink = setContext(async (request, { headers }) => {
  const token = fbAuth.currentUser ? await getIdToken(fbAuth.currentUser) : null;

  if (!token) {
    Sentry.captureMessage('Unauthenticated request', {
      extra: {
        operationName: request.operationName,
        variables: request.variables,
      },
    });

    return { headers };
  }

  return {
    headers: {
      ...headers,
      Authorization: `Bearer ${token}`,
    },
  };
});
